import { useState } from "react";
import Modal from "@/components/base/Modal";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import {
  useTeacherCourses,
  COURSE_ICONS,
  COURSE_COLORS,
} from "@/hooks/useTeacherContent";

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50";

interface CourseForm {
  id: string;
  name: string;
  icon: string;
  color: string;
  totalTopics: number;
  description: string;
}

const emptyForm: CourseForm = {
  id: "",
  name: "",
  icon: COURSE_ICONS[0],
  color: COURSE_COLORS[0],
  totalTopics: 1,
  description: "",
};

export default function Derslerim() {
  const { data, loading, error, reload, saveCourse, deleteCourse } = useTeacherCourses();
  const list = data ?? [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CourseForm>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c: CourseForm) => {
    setForm(c);
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) {
      setFormError("Lütfen ders adını girin.");
      return;
    }
    setBusy(true);
    try {
      await saveCourse({
        id: form.id || undefined,
        name: form.name.trim(),
        icon: form.icon,
        color: form.color,
        totalTopics: form.totalTopics,
        description: form.description.trim(),
      });
      setShowForm(false);
    } catch {
      setFormError("Ders kaydedilirken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      await deleteCourse(confirmId);
      setConfirmId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Derslerim"
          description="Verdiğin dersleri oluştur, düzenle ve yönet."
        />
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          Yeni Ders
        </button>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-book-2-line"
          title="Henüz ders yok"
          description="İlk dersini oluşturmak için 'Yeni Ders' butonuna tıkla."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((course) => (
            <div
              key={course.id}
              className="rounded-xl border border-background-200 bg-background-50 p-5 hover:border-accent-300 transition-colors flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-11 h-11 flex items-center justify-center rounded-lg shrink-0 ${course.color}`}>
                    <i className={`${course.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-base text-foreground-950 truncate">{course.name}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{course.totalTopics} konu</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(course)}
                    className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors"
                    aria-label="Dersi düzenle"
                    title="Düzenle"
                  >
                    <i className="ri-edit-line" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(course.id)}
                    className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                    aria-label="Dersi sil"
                    title="Sil"
                  >
                    <i className="ri-delete-bin-line" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              {course.description && (
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed line-clamp-3">{course.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Ders formu */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={form.id ? "Dersi Düzenle" : "Yeni Ders"}
        titleId="course-form-title"
        id="course-form-modal"
        maxWidthClass="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders Adı</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Örn: TYT Matematik"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Konu Sayısı</label>
              <input
                type="number"
                min={1}
                value={form.totalTopics}
                onChange={(e) => setForm((f) => ({ ...f, totalTopics: Number(e.target.value) }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">İkon</label>
              <select
                value={form.icon}
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                className={inputClass}
              >
                {COURSE_ICONS.map((ic) => (
                  <option key={ic} value={ic}>{ic.replace("ri-", "").replace(/-line/g, "")}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Renk</label>
            <div className="flex flex-wrap gap-2">
              {COURSE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border-2 transition-colors ${c} ${
                    form.color === c ? "border-foreground-900" : "border-transparent"
                  }`}
                  aria-label={`Renk seç: ${c}`}
                >
                  {form.color === c && <i className="ri-check-line" aria-hidden="true"></i>}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Açıklama</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Dersin içeriği hakkında kısa bilgi..."
              className={`${inputClass} h-auto py-2 resize-none`}
            />
          </div>

          {formError && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Silme onayı */}
      <Modal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        title="Dersi Sil"
        titleId="delete-course-title"
        id="delete-course-modal"
        maxWidthClass="max-w-sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            Bu dersi silmek istediğine emin misin? Bu işlem geri alınamaz.
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirmId(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Siliniyor..." : "Sil"}
            </button>
          </div>
        </div>
      </Modal>

      <PanelLinks
        links={[
          { label: "Ders Programım", href: "/ogretmen/ders-programim", icon: "ri-calendar-line" },
          { label: "Canlı Dersler", href: "/ogretmen/canli-dersler", icon: "ri-vidicon-line" },
          { label: "Video Dersler", href: "/ogretmen/video-dersler", icon: "ri-play-circle-line" },
        ]}
      />
    </div>
  );
}